import { useTranslation } from "react-i18next"
import { ArrowSquareOut, Warning } from "@phosphor-icons/react"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { useDePreparationAdditionalInformationQuery$data } from "@/modules/patients/detail/medication/de/services/__generated__/useDePreparationAdditionalInformationQuery.graphql"

type PreparationAdditionalInformationDialogProps = {
  specialistInformation: useDePreparationAdditionalInformationQuery$data["de_preparationSpecialistInformation"]
  letterInformation: useDePreparationAdditionalInformationQuery$data["de_preparationInformationLetter"]
  onOpenChange: (open: boolean) => void
}

export const PreparationAdditionalInformationDialog = ({
  specialistInformation,
  letterInformation,
  onOpenChange,
}: PreparationAdditionalInformationDialogProps) => {
  const { t } = useTranslation()
  const { name, informationTexts } = { ...specialistInformation }
  const { letterNumber, letterList } = letterInformation
  const hasInformation = !!(specialistInformation || !!letterList)

  const handleClose = () => {
    onOpenChange(false)
  }

  const openPDFInNewTab = (base64PDFContent: string) => {
    // Convert base64 to a Blob object
    const byteCharacters = atob(base64PDFContent)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: "application/pdf" })
    const url = URL.createObjectURL(blob)
    window.open(url)
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col p-0" data-testid="preparation-information-dialog-content">
        <DialogHeader className="h-16 shrink-0 border-b pt-4 pr-4 pb-2 pl-6">
          <DialogTitle className="px-0 py-0" data-testid="preparation-information-dialog-title">
            {name}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-hidden">
          <div className="flex h-[73vh] max-h-220.5 justify-between gap-4 px-2 py-4">
            {hasInformation && (
              <>
                <ScrollWrapper className={cn("px-4", { "pr-1": letterNumber > 0 })}>
                  {!!informationTexts?.length &&
                    informationTexts.map((i) => (
                      <div key={i.textId} className="mb-8">
                        <p className="text-on-surface text-base font-medium">{i.title}</p>
                        <PotentiallyUnsafeInnerHtml
                          className="preparation-information text-on-surface-subdued text-sm"
                          value={i.decodedText}
                        />
                      </div>
                    ))}
                </ScrollWrapper>
                {letterNumber > 0 && (
                  <div className="mr-4 flex h-fit w-93.25 flex-col gap-4 rounded-xl border p-4">
                    <div className="text-on-surface flex items-center gap-4">
                      <Warning weight="fill" className="text-critical" />
                      <span className="font-medium">{t("patient.medication.roteHandBrief")}</span>
                    </div>
                    <p className="text-on-surface-subdued text-sm">{t("patient.medication.importantMessage")}</p>
                    <div className="pt-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => openPDFInNewTab(letterList[0].document ?? "")}
                        data-testid="preparation-information-view-pdf-button"
                        asChild>
                        <div className="ml-auto w-fit cursor-pointer">
                          <ArrowSquareOut className="text-on-surface-subdued" />
                          <span>{t("patient.medication.openInNewTab")}</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {!hasInformation && (
              <div className="flex w-full items-center justify-center">
                <p className="text-lg font-bold">{t("patient.medication.noDetailedMedicationInformation")}</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter
          className="h-20 shrink-0 items-center border-t"
          data-testid="preparation-information-dialog-footer">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            data-testid="preparation-information-close-button">
            {t("common.button.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
